import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { X, CreditCard, Loader2, KeyRound } from 'lucide-react';
import api from '../../services/api';
import usePackOffers from '../../hooks/usePackOffers';

let stripePromise = null;
function getStripePromise() {
    if (!stripePromise) {
        stripePromise = api.get('/stripe/config.php')
            .then(({ data }) => loadStripe(data.data.public_key));
    }
    return stripePromise;
}

/**
 * Modal d'achat de packs de codes quiz avec Stripe embedded checkout.
 * Les offres affichées sont celles configurées dans `/gestion/packs`
 * (pack_offers.type = 'quiz_code').
 */
export default function QuizCodeCheckoutModal({ thematiqueId, sousSujetId, sousSujetLabel, label, mode = 'quiz', onClose, onSuccess }) {
    const { offers: PACKS_RAW, loading: offersLoading } = usePackOffers('quiz_code');
    // Shape the offers into the existing { type, nb, price, popular } structure
    // so the rendering code below keeps working without a big refactor.
    const PACKS = PACKS_RAW.map((o, idx) => ({
        type: o.slug.replace(/^qc-/, ''),
        nb: o.nb_codes,
        price: o.price_eur,
        offer_id: o.id,
        popular: idx === Math.floor(PACKS_RAW.length / 2),
    }));
    const [selectedPack, setSelectedPack] = useState(null);
    useEffect(() => {
        if (!selectedPack && PACKS.length > 0) {
            const popular = PACKS.find(p => p.popular) || PACKS[0];
            setSelectedPack(popular.type);
        }
    }, [PACKS, selectedPack]);
    const [clientSecret, setClientSecret] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const isDev = import.meta.env.DEV || new URLSearchParams(window.location.search).has('simulate');
    const [simulating, setSimulating] = useState(false);

    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const handleBuy = async (packType) => {
        setSelectedPack(packType);
        setError(null);
        setLoading(true);
        try {
            const { data } = await api.post('/quiz-code/packs.php', {
                user_thematique_id: thematiqueId,
                sous_sujet_id: sousSujetId,
                pack_type: packType,
                label: label || undefined,
                mode: mode || 'quiz',
            });
            if (data.success) {
                if (data.data.simulated) {
                    setSimulating(true);
                    onSuccess?.();
                    setTimeout(() => onClose(), 1500);
                } else if (data.data.client_secret) {
                    setClientSecret(data.data.client_secret);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
        }
        setLoading(false);
    };

    const handleSimulate = async (packType) => {
        setSimulating(true);
        setError(null);
        try {
            const { data } = await api.post('/quiz-code/packs.php', {
                user_thematique_id: thematiqueId,
                sous_sujet_id: sousSujetId,
                pack_type: packType,
                label: label || undefined,
                simulate: true,
            });
            if (data.data?.simulated) {
                onSuccess?.();
                setTimeout(() => onClose(), 1500);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
            setSimulating(false);
        }
    };

    const pack = PACKS.find(p => p.type === selectedPack);

    return (
        <div className="checkout-modal-overlay" onClick={onClose}>
            <div className="checkout-modal" onClick={e => e.stopPropagation()}>
                <div className="checkout-modal__header">
                    <span className="checkout-modal__title">
                        {clientSecret
                            ? `Paiement — ${pack?.nb} codes quiz (${pack?.price}\u20ac)`
                            : <><KeyRound size={18} /> Codes quiz — {sousSujetLabel}</>
                        }
                    </span>
                    <button className="checkout-modal__close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="checkout-modal__body">
                    {error && <div className="alert alert-error" style={{ margin: '16px' }}>{error}</div>}

                    {simulating && (
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <div className="spinner" style={{ margin: '0 auto 12px' }} />
                            <p><strong>Paiement simul&eacute;</strong> — Codes g&eacute;n&eacute;r&eacute;s !</p>
                        </div>
                    )}

                    {!clientSecret && !simulating && (
                        <div className="sub-modal-plans">
                            <div className="sub-modal-subtitle">
                                Codes quiz pour &laquo;&nbsp;{sousSujetLabel}&nbsp;&raquo;
                            </div>

                            <div className="share-panel__pack-grid">
                                {PACKS.map(p => (
                                    <div key={p.type}
                                        className={`share-panel__pack-card ${p.popular ? 'share-panel__pack-card--popular' : ''} ${selectedPack === p.type ? 'share-panel__pack-card--active' : ''}`}
                                        onClick={() => !loading && setSelectedPack(p.type)}
                                        style={{ cursor: loading ? 'default' : 'pointer' }}>
                                        {p.popular && <span className="share-panel__pack-badge">Populaire</span>}
                                        <KeyRound size={20} />
                                        <div className="share-panel__pack-nb">{p.nb} codes</div>
                                        <div className="share-panel__pack-price">{p.price}&euro;</div>
                                        <div className="share-panel__pack-unit">{(p.price / p.nb * 100).toFixed(0)}c/code</div>
                                    </div>
                                ))}
                            </div>

                            {label && (
                                <div style={{ textAlign: 'center', margin: '8px 0', fontSize: '0.85em', color: 'var(--text-muted)' }}>
                                    Groupe : <strong>{label}</strong>
                                </div>
                            )}

                            <button className="btn btn-primary sub-modal-cta"
                                onClick={() => selectedPack && handleBuy(selectedPack)}
                                disabled={!selectedPack || loading}>
                                {loading
                                    ? <><Loader2 size={16} className="spin" /> Chargement...</>
                                    : <><CreditCard size={16} /> Acheter {pack?.nb} codes</>
                                }
                            </button>
                            {isDev && selectedPack && (
                                <button className="btn btn-sm"
                                    style={{ display: 'block', margin: '8px auto 0', fontSize: '0.75em', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer' }}
                                    onClick={() => handleSimulate(selectedPack)}>
                                    Simuler (dev)
                                </button>
                            )}
                            <p className="sub-modal-note">Paiement unique. Codes g&eacute;n&eacute;r&eacute;s apr&egrave;s paiement. Les participants n'ont pas besoin de cr&eacute;er un compte.</p>
                            <div className="sub-modal-payment-logos">
                                <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-label="Visa"><rect width="38" height="24" rx="3" fill="#1A1F71"/><path d="M15.6 16.2l1.5-9.4h2.4l-1.5 9.4h-2.4zm10.2-9.2c-.5-.2-1.2-.4-2.1-.4-2.3 0-4 1.2-4 3 0 1.3 1.2 2 2.1 2.4.9.4 1.2.7 1.2 1.1 0 .6-.7.9-1.4.9-1 0-1.5-.1-2.3-.5l-.3-.2-.3 2c.6.3 1.6.5 2.7.5 2.5 0 4.1-1.2 4.1-3.1 0-1-.6-1.8-2-2.4-.8-.4-1.3-.7-1.3-1.1 0-.4.4-.8 1.3-.8.7 0 1.3.2 1.7.3l.2.1.4-1.8zM30 16.2l1.9-5.2.5 2.6.3 1.4.2 1.2H30zm3.7-9.4h-1.9c-.6 0-1 .2-1.3.7l-3.6 8.7h2.5l.5-1.4h3.1l.3 1.4H35l-1.3-9.4zM13.5 6.8l-2.3 6.4-.2-1.3c-.4-1.4-1.7-3-3.2-3.7l2.1 7.9h2.5l3.7-9.3h-2.6z" fill="#fff"/><path d="M9.2 6.8H5.6l-.1.2c3 .8 5 2.6 5.8 4.8l-.8-4.2c-.1-.5-.5-.7-1.3-.8z" fill="#F9A533"/></svg>
                                <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-label="Mastercard"><rect width="38" height="24" rx="3" fill="#252525"/><circle cx="15" cy="12" r="7" fill="#EB001B"/><circle cx="23" cy="12" r="7" fill="#F79E1B"/><path d="M19 7.3c1.7 1.2 2.8 3.2 2.8 5.4s-1.1 4.2-2.8 5.4c-1.7-1.2-2.8-3.2-2.8-5.4s1.1-4.2 2.8-5.4z" fill="#FF5F00"/></svg>
                                <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-label="CB"><rect width="38" height="24" rx="3" fill="#1E5CA6"/><text x="19" y="15" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700" fontFamily="Arial">CB</text></svg>
                                <svg viewBox="0 0 24 24" width="16" height="16" role="img" aria-label="Paiement sécurisé"><path d="M12 1L3 5v6c0 5.5 3.8 10.7 9 12 5.2-1.3 9-6.5 9-12V5l-9-4zm0 10.99h7c-.5 4.2-3.1 7.9-7 9.01V12H5V6.3l7-3.1v8.79z" fill="#4CAF50"/></svg>
                                <span className="sub-modal-secure-text">Paiement s&eacute;curis&eacute;</span>
                            </div>
                        </div>
                    )}

                    {clientSecret && !simulating && (
                        <div>
                            <button className="sub-modal-back"
                                onClick={() => { setClientSecret(null); setSelectedPack('50'); setError(null); }}>
                                &larr; Changer de formule
                            </button>
                            <EmbeddedCheckoutProvider stripe={getStripePromise()} options={{ clientSecret }}>
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
